import React, { useCallback } from 'react';
import Chip from '@material-ui/core/Chip';
import { AtomWrapper } from '../AtomWrapper/AtomWrapper';
import { TypeaheadOption } from './parts/types';
import { SelectTypeaheadBase, BaseProps } from './parts/SelectTypeaheadBase';
import * as tokens from '@utmost.co/utmost-design-tokens';
import styled from 'styled-components';
import { RecentItemsProvider } from './parts/RecentItemsProvider';
import { FieldValue } from '../../types';

export interface SelectTypeaheadMultiAtomProps<OptionValue, TargetModel extends any = OptionValue>
  extends Omit<
    BaseProps<OptionValue, TargetModel>,
    'getValue' | 'multiple' | 'readonlyValueLabel' | 'selectedValueLabel'
  > {
  mapInputValue?: (value?: TargetModel) => OptionValue[];
  mapOutputValue?: (values?: OptionValue[]) => TargetModel | undefined;
  onValueChange?: (values: OptionValue[] | undefined) => void;
  value?: TargetModel | null;
  /** Gets the read value label of the select */
  mapReadOnlyValue?: (value?: TargetModel) => (TargetModel extends any[] ? string[] : string) | undefined;
  recentItems?: TypeaheadOption<FieldValue>[];
}

export function SelectTypeaheadMultiAtom<OptionValue extends any = string, TargetModel extends any = OptionValue[]>({
  mapOutputValue,
  mapReadOnlyValue,
  onSetFieldValue,
  classes,
  name,
  value,
  recentItems,
  inputStyle,
  ...props
}: SelectTypeaheadMultiAtomProps<OptionValue, TargetModel> & { classes?: any }) {
  const getValue = useCallback(
    (options?: TypeaheadOption<OptionValue>[]) => {
      const output = options ? [...options] : [];
      const values = output.map((out) => out.value);
      return {
        targetModel: mapOutputValue ? mapOutputValue(values) : (values as TargetModel),
        optionValue: output.map((item) => item.value)
      };
    },
    [mapOutputValue]
  );

  // checking that we can show fallback chips while loading data;
  const readOnlyChips: string[] | undefined =
    mapReadOnlyValue && !!value && !props.values?.length && (props.loading || props.disabled)
      ? (mapReadOnlyValue((props.mapInputValue ? props.mapInputValue(value) : value) as TargetModel) as string[])
      : undefined;
  const removeSelectedChip = useCallback(
    (option: TypeaheadOption<OptionValue>) => {
      const { optionValue, targetModel } = getValue(props.selected?.filter((item) => item.id !== option.id));
      onSetFieldValue(targetModel || (props.nullifyNoneOption ? null : undefined));
      props.onValueChange?.(optionValue as any);
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: exception
    [props, getValue, onSetFieldValue]
  );

  const selectedValueLabel = props.selected?.map((option) => option.label).join(',');
  const readonlyValueLabel = selectedValueLabel ?? (mapReadOnlyValue && !!value ? mapReadOnlyValue(value) : undefined);

  const chips = getChips({
    selectedOptions: props.selected,
    readOnlyChips,
    removeSelectedChip,
    disabled: props.disabled
  });
  return (
    <RecentItemsProvider
      name={name}
      recentItems={recentItems}
      values={props.values}
      isApiDriven={!!props.onFilterChange}
      loading={props.loading}
    >
      <AtomWrapper name={name}>
        <SelectTypeaheadBase<OptionValue, TargetModel>
          getValue={getValue}
          // The chips are the input's startAdornment and share a flex row with the <input>.
          // Wrapping lets the chips take full-width rows with the input flowing below, instead
          // of the input sitting as a second column beside the chips. paddingRight reserves
          // space for the absolutely-positioned trailing icons so chips/input never slide
          // underneath them. Set inline here because SelectComponent overwrites the input root
          // `style`.
          inputStyle={{ flexWrap: 'wrap', paddingRight: 36, ...inputStyle }}
          InputProps={{
            startAdornment: chips !== '' ? <StyledChipWrapper>{chips}</StyledChipWrapper> : undefined,
            classes: {
              root: classes?.root
            },
            inputProps: {
              // Keep the <input> narrow-but-growing so an empty input doesn't claim a full row
              // of its own below the chips.
              style: props.disabled ? { width: '1px' } : { width: 'auto', flex: '1 1 60px', minWidth: 60 }
            }
          }}
          multiple
          selected={props.selected}
          name={name}
          onSetFieldValue={onSetFieldValue}
          readonlyValueLabel={readonlyValueLabel as string}
          {...props}
        />
      </AtomWrapper>
    </RecentItemsProvider>
  );
}

interface GetChips<T> {
  removeSelectedChip: (option: TypeaheadOption<T>) => void;
  selectedOptions?: TypeaheadOption<T>[];
  readOnlyChips?: string[];
  disabled?: boolean;
}

function getChips<T>({ removeSelectedChip, selectedOptions, readOnlyChips, disabled }: GetChips<T>): React.ReactNode {
  if (selectedOptions?.length) {
    const selectedItems = selectedOptions.map((option) => option.label).join(';');
    return (
      <div data-test-multi-selected={selectedItems}>
        {selectedOptions.map((option) => (
          <Chip
            data-test="multi-chip"
            disabled={disabled}
            style={{ margin: 2 }}
            key={option.id}
            label={option.label}
            onDelete={!disabled ? () => removeSelectedChip(option) : undefined}
          />
        ))}
      </div>
    );
  }
  if (readOnlyChips) {
    return (
      <div>
        {readOnlyChips.map((option) => (
          <Chip data-test="multi-chip" style={{ margin: 2 }} key={option} label={option} />
        ))}
      </div>
    );
  }
  return '';
}

const StyledChipWrapper = styled.div`
    width: 100%;

    /* Cap the selected-chips area so a large selection can't grow the field tall enough to
     * squeeze out the dropdown menu; scroll the chips instead. */
    max-height: 168px;
    overflow-y: auto;

    [data-test="multi-chip"].Mui-disabled {
        background-color: ${tokens.stateOnSurfaceDisabledContainerStateLayerColor};
    }
`;
